<template>
  <v-dialog v-model="open" max-width="600px" fullscreen-on-mobile>
    <v-card :loading="loading">
      <v-card-title>Assign Item</v-card-title>
      <v-card-text>
        <v-form ref="assignForm" v-model="assignFormValid">
          <v-autocomplete
            v-model="assignForm.inventoryId"
            :search-input.sync="searchInput"
            label="Item"
            :items="inventoryItems"
            item-text="itemName"
            item-value="id"
            :item-disabled="item => item.availableQty <= 0"
            :loading="inventoryLoading"
            clearable
            no-filter
          >
            <template v-slot:selection="{ item }">
              <v-tooltip bottom v-if="item.itemName && item.itemName.length > 40" max-width="350">
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on" class="autocomplete-selected-item">
                    {{ item.itemName }}
                  </span>
                </template>
                <span class="tooltip-text">{{ item.itemName }}</span>
              </v-tooltip>
              <span v-else class="autocomplete-selected-item">
                {{ item.itemName }}
              </span>
            </template>
            <template v-slot:item="{ item }">
              <div class="d-flex justify-space-between" style="width: 100%">
                <div class="autocomplete-item-text">
                  <v-tooltip bottom v-if="item.itemName && item.itemName.length > 40" max-width="350">
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on" class="autocomplete-item-name">
                        {{ item.itemName }}
                      </span>
                    </template>
                    <span class="tooltip-text">{{ item.itemName }}</span>
                  </v-tooltip>
                  <span v-else class="autocomplete-item-name">
                    {{ item.itemName }}
                  </span>
                  <span v-if="item.size" class="ml-2 text--secondary"
                    >({{ formatItemSize(item.size) }})</span
                  >
                </div>
                <v-chip small :color="item.availableQty > 0 ? 'green' : 'red'" outlined>
                  Available: {{ item.availableQty }}
                </v-chip>
              </div>
            </template>
          </v-autocomplete>

          <v-text-field
            v-model="assignForm.size"
            label="Size"
            disabled
          ></v-text-field>

          <v-text-field
            v-model.number="assignForm.quantity"
            :label="
              selectedInventoryItem
                ? `Quantity (Available: ${selectedInventoryItem.availableQty})`
                : 'Quantity'
            "
            type="number"
            min="1"
            :max="selectedInventoryItem ? selectedInventoryItem.availableQty : 999"
            :rules="quantityRules"
          ></v-text-field>

          <v-menu
            v-model="dateIssuedMenu"
            :close-on-content-click="false"
            :nudge-right="40"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="assignForm.dateIssued"
                label="Date Issued"
                readonly
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="assignForm.dateIssued"
              @input="dateIssuedMenu = false"
            ></v-date-picker>
          </v-menu>

          <v-textarea
            v-model="assignForm.notes"
            label="Notes (optional)"
            rows="3"
            auto-grow
            counter="512"
            maxlength="512"
            :rules="notesRules"
          ></v-textarea>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="open = false" :disabled="loading">Cancel</v-btn>
        <v-btn
          color="primary"
          text
          @click="assignItem"
          :disabled="!assignForm.inventoryId || !assignFormValid || loading"
          :loading="loading"
          >Assign</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Vue from "vue";
import moment from "moment-timezone";
import { mapState } from "vuex";
import { debounce } from "lodash";
import { formatItemSize } from "@/utils/utils";

export default {
  name: "AssignItemDialog",

  data: () => ({
    open: false,
    loading: false,
    driverId: null,

    inventoryItems: [],
    inventoryLoading: false,
    searchInput: null,
    assignFormValid: false,
    dateIssuedMenu: false,

    assignForm: {
      inventoryId: null,
      size: "",
      quantity: null,
      dateIssued: "",
      notes: "",
    },
  }),

  computed: {
    ...mapState("companyModule", ["company"]),

    selectedInventoryItem() {
      return this.inventoryItems.find((i) => i.id === this.assignForm.inventoryId);
    },

    quantityRules() {
      return [
        (v) => !!v || "Quantity is required",
        (v) => v > 0 || "Quantity must be greater than 0",
        (v) => Number.isInteger(Number(v)) || "Quantity must be a whole number",
        (v) => {
          if (this.selectedInventoryItem && v > this.selectedInventoryItem.availableQty) {
            return `Cannot assign more than ${this.selectedInventoryItem.availableQty} items`;
          }
          return true;
        },
      ];
    },

    notesRules() {
      return [
        (v) => !v || v.length <= 512 || "Notes cannot exceed 512 characters",
      ];
    },
  },

  methods: {
    formatItemSize,

    getTodayDate() {
      return moment().tz(this.company.timeZone).format("YYYY-MM-DD");
    },

    async openDialog(driverId) {
      this.driverId = driverId;
      this.loading = false;
      this.open = true;
      this.assignForm.dateIssued = this.getTodayDate();
      await this.loadInventoryItems();
    },

    async assignItem() {
      if (!this.$refs.assignForm.validate()) {
        return;
      }

      this.loading = true;
      try {
        await this.$store.dispatch("assignmentModule/createAssignment", {
          driverId: this.driverId,
          inventoryId: this.assignForm.inventoryId,
          quantity: this.assignForm.quantity,
          dateIssued: moment.tz(this.assignForm.dateIssued, this.company.timeZone).utc().format(),
          notes: this.assignForm.notes || null,
        });

        Vue.toasted.success("Item assigned successfully", { duration: 3000 });
        this.open = false;
        this.resetAssignForm();
      } catch (error) {
        Vue.toasted.error(error, { duration: 5000 });
      } finally {
        this.loading = false;
      }
    },

    async loadInventoryItems(searchString = "") {
      this.inventoryLoading = true;
      try {
        await this.$store.dispatch("inventoryModule/loadPage", {
          index: 0,
          size: 50,
          sort: [],
          searchString: searchString.trim(),
        });
        this.inventoryItems = this.$store.state.inventoryModule.displayInventory;
      } catch (error) {
        Vue.toasted.error("Failed to load inventory items", { duration: 5000 });
      } finally {
        this.inventoryLoading = false;
      }
    },

    searchInventoryItems: debounce(async function (searchString) {
      if (searchString && searchString.trim()) {
        await this.loadInventoryItems(searchString);
      } else {
        await this.loadInventoryItems();
      }
    }, 400),

    resetAssignForm() {
      this.assignForm = {
        inventoryId: null,
        size: "",
        quantity: null,
        dateIssued: this.getTodayDate(),
        notes: "",
      };
      this.searchInput = null;
      if (this.$refs.assignForm) {
        this.$refs.assignForm.resetValidation();
      }
    },
  },

  watch: {
    "assignForm.inventoryId": function (newValue, oldValue) {
      if (newValue) {
        const item = this.inventoryItems.find((i) => i.id === newValue);
        if (item) {
          this.assignForm.size = this.formatItemSize(item.size);
        }

        if (oldValue !== null && oldValue !== undefined) {
          this.assignForm.quantity = null;
          this.assignForm.notes = "";
          this.assignForm.dateIssued = this.getTodayDate();
        }
      } else {
        this.assignForm.size = "";
        this.assignForm.quantity = null;
        this.assignForm.notes = "";
        this.assignForm.dateIssued = this.getTodayDate();
      }
    },

    searchInput(val) {
      if (val !== null && val !== undefined) {
        this.searchInventoryItems(val);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.tooltip-text {
  white-space: normal;
  word-wrap: break-word;
  word-break: break-word;
  line-height: 1.4;
}

.autocomplete-item-text {
  flex: 1;
  min-width: 0;
}

.autocomplete-item-name {
  display: inline-block;
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}

.autocomplete-selected-item {
  display: inline-block;
  max-width: 400px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Hide spin buttons for number input */
::v-deep input[type="number"]::-webkit-outer-spin-button,
::v-deep input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

::v-deep input[type="number"] {
  -moz-appearance: textfield;
}
</style>
